/* eslint @typescript-eslint/no-explicit-any: 0 */
import { newE2EPage } from "@stencil/core/testing";
import type { KeyInput } from "puppeteer/lib/types";

const nestedLists = `
<uic-tab-list class="outer">
    <uic-tab value="shallow-a">SA</uic-tab>
    <uic-tab value="shallow-b">SB</uic-tab>
    <uic-tab-list class="inner">
        <uic-tab value="deep-a">DA</uic-tab>
        <uic-tab value="deep-b">DB</uic-tab>
    </uic-tab-list>
    <uic-tab value="shallow-c">SC</uic-tab>
</uic-tab-list>
`;

describe("tab-list", () => {
    it("renders a component", async () => {
        const page = await newE2EPage();
        await page.setContent(`<uic-tab-list></uic-tab-list>`);
        const el = await page.find("uic-tab-list");
        expect(el).toHaveClass("hydrated");
    });

    it("sets mark on uic-tab selected by its value", async () => {
        const page = await newE2EPage();

        await page.setContent(`
<uic-tab-list>
    <uic-tab value="a"></uic-tab>
    <uic-tab value="b"></uic-tab>
    <uic-tab value="c"></uic-tab>
</uic-tab-list>
`);

        expect(await page.find("uic-tab[value=b]")).not.toHaveAttribute("selected");

        await page.$eval("uic-tab-list", (list: any) => {
            list.value = "b";
        });

        await page.waitForChanges();

        expect(await page.find("uic-tab[value=b]")).toHaveAttribute("selected");
    });

    it("removes the selection when a tab disappears", async () => {
        const page = await newE2EPage();
        await page.setContent(`
<uic-tab-list>
    <uic-tab value="a"></uic-tab>
    <uic-tab value="b"></uic-tab>
    <uic-tab value="c"></uic-tab>
</uic-tab-list>
`);
        // Select a tab
        await page.$eval("uic-tab-list", (list: any) => {
            list.value = "b";
        });

        await page.waitForChanges();
        const list = await page.find("uic-tab-list");
        expect(list.getAttribute("value")).toEqual("b");

        // Remove tab
        await page.$eval("uic-tab[value=b]", (tab) => {
            tab.remove();
        });

        await page.waitForChanges();
        const tabs = await page.findAll("uic-tab");
        expect(tabs.length).toEqual(2);
        expect(tabs.filter((tab) => tab.getAttribute("selected")).length).toEqual(0);
    });

    it("does not remove the selection when a different tab disappears", async () => {
        const page = await newE2EPage();
        await page.setContent(`
<uic-tab-list>
    <uic-tab value="a"></uic-tab>
    <uic-tab value="b"></uic-tab>
    <uic-tab value="c"></uic-tab>
</uic-tab-list>
`);
        // Select a tab
        await page.$eval("uic-tab-list", (list: any) => {
            list.value = "a";
        });

        await page.waitForChanges();
        const list = await page.find("uic-tab-list");
        expect(list.getAttribute("value")).toEqual("a");

        // Remove tab
        await page.$eval("uic-tab[value=b]", (tab) => {
            tab.remove();
        });

        await page.waitForChanges();
        const tabs = await page.findAll("uic-tab");
        expect(tabs.length).toEqual(2);
        expect(list.getAttribute("value")).toEqual("a");
        expect(tabs.find((tab) => tab.getAttribute("value") === "a")).toHaveAttribute("selected");
    });

    it("re-selects a tab when the tab re-appears", async () => {
        const page = await newE2EPage();
        await page.setContent(`
<uic-tab-list>
    <uic-tab value="a"></uic-tab>
    <uic-tab value="b"></uic-tab>
    <uic-tab value="c"></uic-tab>
</uic-tab-list>
`);
        // Select a tab
        await page.$eval("uic-tab-list", (list: any) => {
            list.value = "b";
        });

        await page.waitForChanges();
        const list = await page.find("uic-tab-list");
        expect(list.getAttribute("value")).toEqual("b");

        // Remove tab
        let removedTab;
        await page.$eval("uic-tab[value=b]", (tab) => {
            removedTab = tab;
            tab.remove();
        });

        await page.waitForChanges();
        const tabs = await page.findAll("uic-tab");
        expect(tabs.length).toEqual(2);
        expect(tabs.filter((tab) => tab.getAttribute("selected")).length).toEqual(0);

        // Add the removed tab
        await page.$eval("uic-tab-list", (list) => {
            list.appendChild(removedTab);
        });

        await page.waitForChanges();
        const tabsAfterAdding = await page.findAll("uic-tab");
        expect(tabsAfterAdding.length).toEqual(3);
        expect(tabsAfterAdding.find((tab) => tab.getAttribute("value") === "b")).toHaveAttribute("selected");
    });

    it("updates its selection in a complex setup", async () => {
        const page = await newE2EPage();

        await page.setContent(nestedLists);

        expect(await page.find("uic-tab[value=shallow-b]")).not.toHaveAttribute("selected");

        await page.$eval(".outer", (list: any) => {
            list.value = "shallow-b";
        });

        await page.waitForChanges();

        expect(await page.find("uic-tab[value=shallow-b]")).toHaveAttribute("selected");
    });

    it("has single selection in a complex setup, only", async () => {
        const page = await newE2EPage();
        await page.setContent(nestedLists);

        expect((await page.findAll("uic-tab[selected]")).length).toEqual(0);

        await page.$eval(".outer", (list: any) => {
            list.value = "shallow-b";
        });
        await page.waitForChanges();

        expect((await page.findAll("uic-tab[selected]")).length).toEqual(1);

        await page.$eval(".inner", (list: any) => {
            list.value = "deep-a";
        });
        await page.waitForChanges();

        expect((await page.findAll("uic-tab[selected]")).length).toEqual(1);
    });

    it("implicitly marks shallow uic-tab as selected when clicking it", async () => {
        const page = await newE2EPage();

        await page.setContent(nestedLists);

        const list = await page.find(".outer");
        const tab = await page.find("uic-tab[value=shallow-b]");

        expect(list.getAttribute("value")).not.toEqual("shallow-b");
        expect(tab).not.toHaveAttribute("selected");

        await page.click("uic-tab[value=shallow-b]");
        await page.waitForChanges();

        expect(list.getAttribute("value")).toEqual("shallow-b");
        expect(tab).toHaveAttribute("selected");
    });

    it("implicitly marks nested uic-tab as selected when clicking it", async () => {
        const page = await newE2EPage();

        await page.setContent(nestedLists);

        const list = await page.find(".outer");
        const tab = await page.find("uic-tab[value=deep-a]");

        expect(list.getAttribute("value")).not.toEqual("deep-a");
        expect(tab).not.toHaveAttribute("selected");

        await page.click("uic-tab[value=deep-a]");
        await page.waitForChanges();

        expect(list.getAttribute("value")).toEqual("deep-a");
        expect(tab).toHaveAttribute("selected");
    });

    it("changes selected tab via keyboard", async () => {
        const page = await newE2EPage();
        await page.setContent(nestedLists);
        await page.click("uic-tab[value=deep-a]");
        await page.focus("uic-tab[value=deep-a]");
        await page.waitForChanges();

        const list = await page.find(".outer");
        expect(list.getAttribute("value")).toEqual("deep-a");

        for (const confirm of ["Space", "Enter"]) {
            for (const value of ["deep-b", "shallow-c", "shallow-a", "shallow-b", "deep-a"]) {
                await page.keyboard.press("ArrowRight");
                await page.keyboard.press(confirm as KeyInput);
                await page.waitForChanges();

                expect(list.getAttribute("value")).toEqual(value);
            }
        }

        for (const confirm of ["Space", "Enter"]) {
            for (const value of ["shallow-b", "shallow-a", "shallow-c", "deep-b", "deep-a"]) {
                await page.keyboard.press("ArrowLeft");
                await page.keyboard.press(confirm as KeyInput);
                await page.waitForChanges();

                expect(list.getAttribute("value")).toEqual(value);
            }
        }
    });

    it("changes selected tab via `value` attribute of list", async () => {
        const page = await newE2EPage();
        await page.setContent(nestedLists);
        const values = ["shallow-a", "shallow-b", "deep-a", "deep-b", "shallow-c"];

        for (const value of values) {
            await page.$eval(
                ".outer",
                (list: HTMLUicTabListElement, value: string) => {
                    list.value = value;
                },
                value
            );
            await page.waitForChanges();

            for (const key of values) {
                const el = await page.find(`uic-tab[value=${key}]`);

                if (key === value) {
                    expect(el).toHaveAttribute("selected");
                } else {
                    expect(el).not.toHaveAttribute("selected");
                }
            }
        }
    });
});
